import { gql } from '@apollo/client';

/**
 * Spec 031. Every field is written out in each document: codegen skips documents
 * built with `${}` interpolation.
 * Platform on web is always "web".
 */

export const POLICY_DOCUMENT = gql`
  query PolicyDocument($kind: PolicyKind!) {
    policyDocument(kind: $kind) {
      id
      kind
      scope
      title
      versionNumber
      status
      effectiveDate
      contentMarkdown
      contentSha256
      publishedAt
      approximateDates
      materialChange
      changeSummary
    }
  }
`;

export const POLICY_DOCUMENTS = gql`
  query PolicyDocuments {
    policyDocuments {
      kind
      title
      ownedByBrand
      current {
        id
        kind
        scope
        title
        versionNumber
        status
        effectiveDate
        contentMarkdown
        contentSha256
        publishedAt
        approximateDates
        materialChange
        changeSummary
      }
    }
  }
`;

export const POLICY_VERSION = gql`
  query PolicyVersion($id: ID!) {
    policyVersion(id: $id) {
      id
      kind
      scope
      title
      versionNumber
      status
      effectiveDate
      contentMarkdown
      contentSha256
      publishedAt
      approximateDates
      materialChange
      changeSummary
    }
  }
`;

export const MY_PENDING_POLICIES = gql`
  query MyPendingPolicies {
    myPendingPolicies {
      kind
      version {
        id
        kind
        scope
        title
        versionNumber
        status
        effectiveDate
        contentMarkdown
        contentSha256
        publishedAt
        approximateDates
        materialChange
        changeSummary
      }
      previous {
        id
        kind
        title
        versionNumber
        effectiveDate
      }
    }
  }
`;

export const ACCEPT_BOOKING_POLICIES = gql`
  mutation AcceptBookingPolicies($bookingId: ID!, $input: AcceptPoliciesInput!) {
    acceptBookingPolicies(bookingId: $bookingId, input: $input) {
      id
      kind
      context
      method
      acceptedAt
      brandSite
      bookingId
      version {
        id
        kind
        title
        versionNumber
        effectiveDate
      }
    }
  }
`;

export const ACCEPT_EXTENSION_POLICIES = gql`
  mutation AcceptExtensionPolicies($bookingId: ID!, $input: AcceptPoliciesInput!) {
    acceptExtensionPolicies(bookingId: $bookingId, input: $input) {
      id
      kind
      context
      method
      acceptedAt
      brandSite
      bookingId
      version {
        id
        kind
        title
        versionNumber
        effectiveDate
      }
    }
  }
`;

export const ACCEPT_ADDITIONAL_DRIVER_AGREEMENT = gql`
  mutation AcceptAdditionalDriverAgreement($bookingId: ID!, $input: AcceptPoliciesInput!) {
    acceptAdditionalDriverAgreement(bookingId: $bookingId, input: $input) {
      id
      kind
      context
      method
      acceptedAt
      brandSite
      bookingId
      version {
        id
        kind
        title
        versionNumber
        effectiveDate
      }
    }
  }
`;

export const ACCEPT_PENDING_POLICIES = gql`
  mutation AcceptPendingPolicies($input: AcceptPoliciesInput!) {
    acceptPendingPolicies(input: $input) {
      id
      kind
      context
      method
      acceptedAt
      brandSite
      version {
        id
        kind
        title
        versionNumber
        effectiveDate
      }
    }
  }
`;

export const PUBLISHER_ACKNOWLEDGMENT = gql`
  query PublisherAcknowledgment {
    publisherAcknowledgment {
      id
      kind
      scope
      title
      versionNumber
      status
      effectiveDate
      contentMarkdown
      contentSha256
      publishedAt
      approximateDates
      materialChange
      changeSummary
    }
  }
`;

export const ADMIN_POLICY_DOCUMENTS = gql`
  query AdminPolicyDocuments($scope: String) {
    adminPolicyDocuments(scope: $scope) {
      kind
      scope
      title
      ownedByBrand
      current {
        id
        kind
        scope
        title
        versionNumber
        status
        effectiveDate
        contentMarkdown
        contentSha256
        publishedAt
        approximateDates
        materialChange
        changeSummary
        publishedBy {
          id
          name
          email
        }
        publishAcknowledgment {
          id
          acceptedAt
          version {
            id
            versionNumber
          }
        }
      }
      draft {
        id
        kind
        scope
        title
        versionNumber
        status
        effectiveDate
        contentMarkdown
        contentSha256
        approximateDates
        materialChange
        changeSummary
      }
      defaultVersion {
        id
        kind
        title
        versionNumber
        effectiveDate
        contentMarkdown
        contentSha256
      }
    }
  }
`;

export const ADMIN_POLICY_VERSIONS = gql`
  query AdminPolicyVersions($kind: PolicyKind!, $scope: String) {
    adminPolicyVersions(kind: $kind, scope: $scope) {
      id
      kind
      scope
      title
      versionNumber
      status
      effectiveDate
      contentMarkdown
      contentSha256
      publishedAt
      supersededAt
      approximateDates
      materialChange
      changeSummary
      publishedBy {
        id
        name
        email
      }
      publishAcknowledgment {
        id
        acceptedAt
        version {
          id
          versionNumber
        }
      }
    }
  }
`;

export const ADMIN_POLICY_EVENTS = gql`
  query AdminPolicyEvents($kind: PolicyKind!, $scope: String, $limit: Int) {
    adminPolicyEvents(kind: $kind, scope: $scope, limit: $limit) {
      id
      scope
      kind
      versionId
      action
      actorId
      actorName
      requestBrandSite
      createdAt
    }
  }
`;

export const ADMIN_POLICY_ACCEPTANCES = gql`
  query AdminPolicyAcceptances($filter: PolicyAcceptanceFilter!) {
    adminPolicyAcceptances(filter: $filter) {
      nextBefore
      rows {
        id
        kind
        context
        method
        acceptedAt
        brandSite
        bookingId
        userId
        userEmail
        platform
        ipAddress
        userAgent
        version {
          id
          kind
          title
          versionNumber
          effectiveDate
        }
      }
    }
  }
`;

export const ADMIN_POLICY_ACCEPTANCES_CSV = gql`
  query AdminPolicyAcceptancesCsv($filter: PolicyAcceptanceFilter!) {
    adminPolicyAcceptancesCsv(filter: $filter)
  }
`;

export const SAVE_POLICY_DRAFT = gql`
  mutation SavePolicyDraft($input: SavePolicyDraftInput!) {
    savePolicyDraft(input: $input) {
      id
      kind
      scope
      title
      versionNumber
      status
      effectiveDate
      contentMarkdown
      contentSha256
      changeSummary
      materialChange
    }
  }
`;

export const DISCARD_POLICY_DRAFT = gql`
  mutation DiscardPolicyDraft($draftId: ID!) {
    discardPolicyDraft(draftId: $draftId)
  }
`;

export const PUBLISH_POLICY_VERSION = gql`
  mutation PublishPolicyVersion($input: PublishPolicyVersionInput!) {
    publishPolicyVersion(input: $input) {
      id
      kind
      scope
      title
      versionNumber
      status
      effectiveDate
      contentMarkdown
      contentSha256
      publishedAt
      approximateDates
      materialChange
      changeSummary
      publishAcknowledgment {
        id
        acceptedAt
        version {
          id
          versionNumber
        }
      }
    }
  }
`;

/** Trip agreement fields. One document, so the fragment needs no interpolation. */
export const BOOKING_POLICY_RECORD = gql`
  fragment BookingPolicyFields on Booking {
    acceptedPolicies {
      id
      kind
      context
      method
      acceptedAt
      brandSite
      bookingId
      version {
        id
        kind
        title
        versionNumber
        effectiveDate
        contentSha256
      }
    }
    governingAgreement {
      status
      acceptedAt
      version {
        id
        kind
        scope
        title
        versionNumber
        status
        effectiveDate
        contentMarkdown
        contentSha256
        approximateDates
        materialChange
      }
    }
    agreementRecords {
      id
      sequence
      reason
      authorization
      payload
      payloadSha256
      previousSha256
      createdAt
      governingVersion {
        id
        kind
        title
        versionNumber
        effectiveDate
        contentMarkdown
        contentSha256
        approximateDates
      }
    }
    additionalDriverAcceptances {
      driverId
      name
      accepted
      acceptedAt
    }
  }

  query BookingPolicyRecord($id: String!) {
    booking(id: $id) {
      id
      ...BookingPolicyFields
    }
  }
`;
